import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { LoginForm } from '@/components/LoginForm';
import { Logo } from '@/components/Logo';
import { useTheme } from '@/components/ThemeProvider';
import { Button } from '@/components/ui/button';

import { useQuery } from 'hooks/query';

import { ChainlitContext, useAuth } from 'client-types/*';
import { useAzureAuth } from '@/hooks/useAzureAuth';

export const LoginError = new Error(
  'Error logging in. Please try again later.'
);

export default function Login() {
  const query = useQuery();
  const { data: config, user, setUserFromAPI } = useAuth();
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  const { variant } = useTheme();
  const isDarkMode = variant === 'dark';
  
  // Azure authentication
  const { login: azureLogin, isLoading: azureLoading, error: azureError, accessToken } = useAzureAuth();

  const handleCookieAuth = (json: any): void => {
    if (json?.success != true) throw LoginError;

    // Validate login cookie and get user data.
    setUserFromAPI();
  };

  const handleAuth = async (
    jsonPromise: Promise<any>,
    redirectURL?: string
  ) => {
    try {
      const json = await jsonPromise;

      handleCookieAuth(json);

      if (redirectURL) {
        navigate(redirectURL);
      }
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleHeaderAuth = async () => {
    const jsonPromise = apiClient.headerAuth();
    handleAuth(jsonPromise, '/');
  };

  const handleMicrosoftSignIn = async () => {
    console.log('='.repeat(80));
    console.log('[Login] 🔐 Microsoft sign-in button clicked');
    console.log('[Login] Azure loading state:', azureLoading);
    console.log('[Login] Azure access token present:', !!accessToken);
    
    try {
      setIsLoggingIn(true);
      setError('');
      
      console.log('[Login] Calling azureLogin()...');
      await azureLogin();
      console.log('[Login] ✅ azureLogin() completed successfully');
      
      // After successful Azure login, authenticate with backend
      console.log('[Login] Calling apiClient.headerAuth()...');
      const jsonPromise = apiClient.headerAuth();
      
      console.log('[Login] Calling handleAuth with redirect to "/"...');
      await handleAuth(jsonPromise, '/');
      console.log('[Login] ✅ handleAuth completed successfully');
      
    } catch (err: any) {
      console.error('[Login] ❌ Microsoft sign-in failed');
      console.error('[Login] Error:', err);
      console.error('[Login] Error message:', err?.message);
      console.error('[Login] Error details:', JSON.stringify(err, null, 2));
      setError(err?.message || 'Failed to sign in with Microsoft');
    } finally {
      console.log('[Login] Setting isLoggingIn to false');
      setIsLoggingIn(false);
      console.log('='.repeat(80));
    }
  };

  useEffect(() => {
    setError(query.get('error') || azureError || '');
  }, [query, azureError]);

  useEffect(() => {
    if (!config) {
      return;
    }
    if (!config.requireLogin) {
      navigate('/');
    }
    // If header auth is enabled and we have an access token, authenticate
    if (config.headerAuth && accessToken) {
      handleHeaderAuth();
    }
    if (user) {
      navigate('/');
    }
  }, [config, user, accessToken]);

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Logo className="w-[150px]" />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            {/* Microsoft Sign In Button */}
            <div className="flex flex-col gap-4">
              <h2 className="text-2xl font-semibold text-center mb-4">Sign in</h2>
              
              {error && (
                <div className="text-sm text-destructive text-center p-2 border border-destructive rounded">
                  {error}
                </div>
              )}
              
              <Button
                onClick={handleMicrosoftSignIn}
                disabled={isLoggingIn || azureLoading}
                className="w-full"
                size="lg"
              >
                {(isLoggingIn || azureLoading) ? (
                  <>
                    <span className="mr-2">⏳</span>
                    Signing in...
                  </>
                ) : (
                  <>
                    <svg className="mr-2 h-5 w-5" viewBox="0 0 21 21" fill="none">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                    </svg>
                    Sign in with Microsoft
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {!config?.headerAuth ? (
        <div className="relative hidden bg-muted lg:block overflow-hidden">
          <img
            src={
              config?.ui?.login_page_image ||
              apiClient.buildEndpoint('/favicon')
            }
            alt="Image"
            className={`absolute inset-0 h-full w-full object-cover ${
              isDarkMode
                ? config?.ui?.login_page_image_dark_filter ||
                  'brightness-[0.2] grayscale'
                : config?.ui?.login_page_image_filter || ''
            }`}
          />
        </div>
      ) : null}
    </div>
  );
}
